class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.setupShippingProgress();
  }

  setupShippingProgress() {
    // Wait for dependencies to be available
    if (
      typeof subscribe === "undefined" ||
      typeof PUB_SUB_EVENTS === "undefined"
    ) {
      // Retry after a short delay if dependencies aren't loaded yet
      setTimeout(() => this.setupShippingProgress(), 100);
      return;
    }

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event && event.cartData) {
          this.updateShippingProgress(event.cartData);
        }
      }
    );
  }

  updateShippingProgress(cartData) {
    const progressBar = this.querySelector(".cart-drawer__shipping-progress");
    if (!progressBar) return;

    let cartTotal = cartData.total_price || cartData.totalPrice || 0;
    
    // Exclude persistent product from progress calculation
    const persistentProductInput = document.querySelector('[data-persistent-product="true"]');
    if (persistentProductInput && cartData.items) {
      const persistentVariantId = persistentProductInput.dataset.quantityVariantId;
      if (persistentVariantId) {
        const persistentItem = cartData.items.find(
          (item) => item.variant_id.toString() === persistentVariantId.toString()
        );
        if (persistentItem) {
          cartTotal = cartTotal - persistentItem.final_line_price;
        }
      }
    }
    
    // Update the data attribute to keep it in sync
    if (cartTotal > 0) {
      progressBar.dataset.cartTotal = cartTotal;
    }
    const threshold =
      parseInt(progressBar.dataset.freeShippingThreshold) || 5000;

    // Update message
    const messageElement = progressBar.querySelector(
      ".shipping-progress__text"
    );
    if (messageElement) {
      if (cartTotal >= threshold) {
        messageElement.textContent = "You qualify for free shipping!";
      } else {
        const amountNeeded = threshold - cartTotal;
        const formattedAmount = this.formatMoney(amountNeeded);
        messageElement.textContent = `Add ${formattedAmount} more for free shipping!`;
      }
    }

    // Update progress fill
    const progressFill = progressBar.querySelector(".shipping-progress__fill");
    if (progressFill && threshold > 0) {
      const progressPercentage = Math.min((cartTotal / threshold) * 100, 100);
      progressFill.style.width = `${progressPercentage}%`;
    }

    // Update checkpoint state
    const checkpoint = progressBar.querySelector(
      ".shipping-progress__checkpoint"
    );
    if (checkpoint) {
      if (cartTotal >= threshold) {
        checkpoint.classList.add("shipping-progress__checkpoint--achieved");
      } else {
        checkpoint.classList.remove("shipping-progress__checkpoint--achieved");
      }
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.money_format || "{{amount}}");
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      // Update shipping progress if cart data is available
      if (parsedState && parsedState.item_count !== undefined) {
        this.updateShippingProgress(parsedState);
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
